import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva } from 'class-variance-authority';
import { useDirection } from 'radix-ui/direction';
import type { ComponentProps } from 'react';
import type { DrawerContentProps } from './Drawer';
import type { DrawerDirection } from './DrawerPrimitive';
import * as DrawerPrimitive from './DrawerPrimitive';

export const DrawerTriggerSlot = Slot<ComponentProps<typeof DrawerPrimitive.Trigger>>({
  displayName: 'Drawer.Trigger',
  preventOverride: true,
  renderDefault: ({ asChild = true, ...props }) => (
    <DrawerPrimitive.Trigger asChild={asChild} data-spellbook-name="drawer-trigger" {...props} />
  ),
});

export const DrawerTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & {
    'data-testid'?: string;
  }
>({
  displayName: 'Drawer.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: ({ className, children, side = 'top', align = 'center', 'data-testid': dataTestId, ...props }) => {
    const { slots } = useSlots(children, {
      tooltipContent: { Component: DrawerTooltipContentSlot },
      trigger: { Component: DrawerTriggerSlot, props: { 'data-testid': dataTestId } },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} asToggleTip={false} {...props}>
        <Tooltip.Trigger>{slots.trigger}</Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const DrawerTooltipContentSlot = Slot<TextProps>({
  displayName: 'Drawer.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => <Text data-spellbook-name="drawer-tooltip-content" variant="caption-1" element="span" {...props} />,
});

export const DrawerHeaderSlot = Slot<TextProps>({
  displayName: 'Drawer.Header',
  preventOverride: true,
  renderDefault: (props) => <Text element="h2" variant="heading-3" tabIndex={-1} {...props} />,
});

const drawerContentVariants = cva(
  [
    'fixed',
    'inset-x-0',
    'z-drawer',
    'flex',
    'flex-col',
    'bg-surface-background-0',
    'shadow-overlay',
    'border-border-default',
    'w-full',
    'min-h-sm',
  ],
  {
    variants: {
      /** Direction of the drawer. */
      direction: {
        top: [
          'sm:top-0',
          'bottom-auto',
          'sm:left-0',
          'sm:right-0',
          'sm:translate-x-0',
          'sm:translate-y-0',
          'h-auto',
          'max-h-[100lvh]',
          'sm:max-h-[calc(100lvh-16rem)]',
          'border-b',
        ],
        bottom: [
          'sm:top-auto',
          'sm:left-0',
          'sm:right-0',
          'bottom-0',
          'sm:translate-x-0',
          'sm:translate-y-0',
          'h-auto',
          'max-h-[100lvh]',
          'sm:max-h-[calc(100lvh-16rem)]',
          'border-t',
        ],
        left: [
          'top-0',
          'left-0',
          'sm:right-auto',
          'sm:translate-x-0',
          'sm:translate-y-0',
          'h-full',
          'max-w-[100lvw]',
          'sm:min-w-sm',
          'w-auto',
          'sm:max-w-[calc(100lvw-16rem)]',
          'border-r',
        ],
        right: [
          'top-0',
          'right-0',
          'sm:left-auto',
          'sm:translate-x-0',
          'sm:translate-y-0',
          'h-full',
          'max-w-[100lvw]',
          'sm:min-w-sm',
          'w-auto',
          'sm:max-w-[calc(100lvw-16rem)]',
          'border-l',
        ],
      },
    },
  },
);

const drawerHeaderVariants = cva('h-20 flex items-center px-6 py-4 rtl:text-right min-w-0 max-w-2xl', {
  variants: {
    hasActions: {
      true: 'mb-18 sm:mb-0',
    },
    hideHeader: {
      true: 'sr-only',
    },
    disableCloseIcon: {
      false: 'pr-16',
    },
  },
});

export const DrawerOverlaySlot = Slot<
  ComponentProps<typeof DrawerPrimitive.Overlay> &
    DrawerContentProps & {
      contentClassName?: string;
      direction?: DrawerDirection;
      children?: React.ReactNode;
    }
>({
  displayName: 'Drawer.Overlay',
  isRenderedByDefault: true,
  preventOverride: true,
  renderDefault: ({
    className,
    contentClassName,
    children,
    direction,
    style,
    hideHeader,
    headerClassName,
    disableCloseIcon,
    returnFocusRef,
    ...props
  }) => {
    const dir = useDirection();

    const { slots } = useSlots(children, {
      header: { Component: DrawerHeaderSlot },
      content: { Component: DrawerContentSlot },
      footer: { Component: DrawerFooterSlot },
      close: { Component: DrawerCloseSlot },
      headerActions: { Component: DrawerHeaderActionsSlot },
    });

    if (!slots.header) console.warn('Drawer requires a Header slot.');

    return (
      <DrawerPrimitive.Overlay
        data-spellbook-name="drawer-overlay"
        className={cn(['fixed', 'inset-0', 'z-drawer', 'bg-surface-backdrop-0'], className)}
        {...props}
      >
        <DrawerPrimitive.Content
          aria-describedby={undefined}
          dir={dir}
          data-drawer-direction={direction}
          data-spellbook-name="drawer-content"
          className={cn(drawerContentVariants({ direction }), contentClassName)}
          style={style}
          // Radix returns focus to a Trigger; an opener elsewhere in the layout has to be named.
          onCloseAutoFocus={
            returnFocusRef
              ? (event) => {
                  event.preventDefault();
                  returnFocusRef.current?.focus();
                }
              : undefined
          }
        >
          <div
            data-spellbook-name="drawer-header"
            className={cn(
              drawerHeaderVariants({ hasActions: Boolean(slots.headerActions), hideHeader, disableCloseIcon }),
              headerClassName,
            )}
          >
            <DrawerPrimitive.Title asChild className="truncate" data-spellbook-name="drawer-title">
              {slots.header}
            </DrawerPrimitive.Title>
          </div>
          {slots.content}
          {slots.footer}
          {slots.headerActions}
          {!disableCloseIcon && (
            <DrawerPrimitive.Close
              aria-label="Close"
              asChild
              className="absolute top-3 ltr:right-2 rtl:left-2"
              data-spellbook-name="drawer-close"
            >
              <IconButton name="x" shape="circle" color="neutral" variant="ghost" size="sm" />
            </DrawerPrimitive.Close>
          )}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Overlay>
    );
  },
});

export const DrawerContentSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Drawer.Content',
  renderDefault: ({ className, children, ...props }) => {
    return (
      <div className={cn('ps-6 pe-3 flex flex-col gap-4 w-full sm:grow overflow-y-auto [scrollbar-gutter:stable]', className)} {...props}>
        {children}
      </div>
    );
  },
});

export const DrawerFooterSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Drawer.Footer',
  renderDefault: ({ className, ...props }) => (
    <div className={cn('flex flex-col gap-4 sm:flex-row-reverse px-6 py-4', className)} data-spellbook-name="drawer-footer" {...props} />
  ),
});

export const DrawerCloseSlot = Slot<ComponentProps<typeof DrawerPrimitive.Close>>({
  displayName: 'Drawer.Close',
  preventOverride: true,
  renderDefault: ({ asChild = true, ...props }) => (
    <DrawerPrimitive.Close data-spellbook-name="drawer-close" asChild={asChild} {...props} />
  ),
});

export const DrawerHeaderActionsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'Drawer.HeaderActions',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <div
      className={cn(
        [
          'flex',
          'gap-4',
          'px-6',
          '[&_*]:grow',
          'items-stretch',
          'justify-center',
          'sm:justify-end',
          'absolute',
          'top-18',
          'sm:px-0',
          'sm:top-4',
          'left-0',
          'ltr:sm:left-auto',
          'rtl:sm:right-auto',
          'right-0',
          'ltr:sm:right-16',
          'rtl:sm:left-16',
          'py-1.5',
          'sm:py-0',
        ],
        className,
      )}
      data-spellbook-name="drawer-header-actions"
      {...props}
    />
  ),
});
